import React from "react";
import { Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const NUM = /\d|€|\$|%/;
const CONTEXT = /\b(chi|dove|quando|perch[eé]|oggi|attualmente|nel|nella|per (i|le|gli)|who|where|when|why|because|today|currently|in (the|my|our))\b/i;
const EXAMPLE = /\b(es\.|esempio|ad esempio|per esempio|come|tipo|e\.g\.|for example|for instance|like|such as)\b|["«»“”]/i;

/** Real-time, heuristic completeness indicator. Never blocks: it only informs. */
export function answerQuality(text) {
  const t = (text || "").trim();
  const words = t ? t.split(/\s+/).length : 0;
  const checks = {
    depth: words >= 25,
    context: CONTEXT.test(t) || (t.split(/[.!?]/).filter((x) => x.trim().length > 15).length >= 2 && words >= 15),
    numbers: NUM.test(t),
    examples: EXAMPLE.test(t),
  };
  const score = Object.values(checks).filter(Boolean).length;
  return { checks, score, words, level: score >= 4 ? "complete" : score >= 2 ? "good" : words > 0 ? "draft" : "empty" };
}

export function AnswerQuality({ text }) {
  const { t } = useLang();
  const q = answerQuality(text);
  if (q.level === "empty") return null;
  const color = q.level === "complete" ? "#16a34a" : q.level === "good" ? "#7c3aed" : "#f59e0b";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px]" data-testid="answer-quality" aria-live="polite">
      <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color }} data-testid="answer-quality-level">
        <span className="inline-flex gap-0.5">{[0, 1, 2, 3].map((i) => <span key={i} className="h-1.5 w-3 rounded-full" style={{ background: i < q.score ? color : "#e2e8f0" }} />)}</span>
        {t(`flow.quality_${q.level}`)}
      </span>
      {["depth", "context", "numbers", "examples"].map((k) => (
        <span key={k} className={`inline-flex items-center gap-1 ${q.checks[k] ? "text-slate-700" : "text-slate-400"}`} data-testid={`answer-quality-${k}`} data-ok={q.checks[k]}>
          <span className={`h-3.5 w-3.5 rounded-full border inline-flex items-center justify-center ${q.checks[k] ? "bg-[#0b0f1a] border-[#0b0f1a] text-white" : "border-slate-300"}`}>{q.checks[k] && <Check size={9} />}</span>
          {t(`flow.quality_${k}`)}
        </span>
      ))}
      <span className="text-slate-400">{t("flow.quality_free")}</span>
    </div>
  );
}
